import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
    CheckCircleRounded,
    InsertDriveFileRounded,
    Info,
    PlayArrowRounded,
    Schedule,
    Visibility,
} from '@mui/icons-material'
import { Box, Chip, CircularProgress, Collapse, useTheme } from '@mui/material'
import CustomLinearProgress from '../../../../components/muiComponents/customLinearProgress'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { padTo2Digits, toMinutesAndSeconds } from '../../../../utils/functions'
import {
    PassedTestState,
    WatchedVideoLessonState,
    WatchedVideoLessonTimeState,
} from '../../../../features/types'

interface Props {
    section: PassedTestState | WatchedVideoLessonState
    studentId: number
    isLast: boolean
    type: string
}

const Section: React.FC<Props> = ({ section, studentId, type, isLast }) => {
    const { formatMessage } = useIntl()
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const { data: videoLessonAnalysis = [], isFetching } =
        videoLessonApiSlice.useGetStudentWatchedVideoTimeAnalysisQuery(
            {
                videoLessonId: (section as WatchedVideoLessonState)
                    .videoLessonId,
                studentId,
            },
            { skip: !expanded },
        )

    const styles = {
        wrapper: {
            p: 1,
            borderBottom: isLast ? '' : `1px solid ${theme.palette.grey[200]}`,
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        typeIcon: {
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
        },
        title: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        linearProgress: {
            backgroundcolor: '#E6F0FF',
            barbackgroundcolor: '#2F80ED',
        },
        progressMargin: {
            marginTop: '8px',
        },
        fullWidth: {
            width: '100%',
        },
        viewChip: {
            ml: 1,
            mr: 1,
            p: '0px 4px',
            cursor: 'pointer',
        },
        timeContainer: {
            background: theme.palette.grey[100],
            display: 'flex',
            justifyContent: 'space-between',
            borderRadius: '8px',
            mt: 2,
            p: 2,
            maxHeight: '160px',
            overflow: 'auto',
        },
        timeTitleBox: {
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
        },
    }

    const TypeIcon = () =>
        type === 'VIDEO' ? <PlayArrowRounded sx={{ color: '#2F80ED' }} /> : <InsertDriveFileRounded sx={{ color: '#2F80ED' }} />

    const getVisitedDate = (video: WatchedVideoLessonTimeState) => {
        return (
            padTo2Digits(new Date(video.visitedDateAndTime).getDate()) +
            ' | ' +
            padTo2Digits(new Date(video.visitedDateAndTime).getMonth()) +
            ' | ' +
            new Date(video.visitedDateAndTime).getFullYear()
        )
    }

    const getVisitedTime = (video: WatchedVideoLessonTimeState) => {
        return (
            padTo2Digits(new Date(video.visitedDateAndTime).getHours()) +
            ' : ' +
            padTo2Digits(new Date(video.visitedDateAndTime).getMinutes())
        )
    }

    const getDuration = (video: WatchedVideoLessonTimeState) => {
        return toMinutesAndSeconds(video.watchedVideoTime)
    }

    return (
        <Box sx={styles.wrapper}>
            <Box sx={{ ...styles.flexCenter, ...styles.fullWidth }}>
                <Box sx={styles.typeIcon}>
                    <TypeIcon />
                </Box>

                <Box sx={styles.fullWidth}>
                    <Box sx={styles.title}>
                        <Box>{section.title}</Box>

                        <Box sx={styles.flexCenter}>
                            {section.percentage}%
                        </Box>
                    </Box>

                    <CustomLinearProgress
                        variant="determinate"
                        value={section.percentage}
                        {...styles.linearProgress}
                        style={styles.progressMargin}
                    />
                </Box>

                {type === 'VIDEO' && (
                    <Chip
                        sx={styles.viewChip}
                        label={
                            (section as WatchedVideoLessonState)
                                .watchedVideoCount
                        }
                        onClick={() => setExpanded(!expanded)}
                        avatar={<Visibility />}
                    />
                )}
            </Box>

            <Collapse in={expanded}>
                {isFetching ? (
                    <CircularProgress sx={{ m: 1 }} />
                ) : (
                    <Box sx={styles.timeContainer}>
                        <Box mr={8}>
                            <Box sx={styles.timeTitleBox}>
                                <Box mr={1}>
                                    {formatMessage({ id: 'visitedDate' })}
                                </Box>

                                <Info
                                    fontSize="small"
                                    color="disabled"
                                />
                            </Box>

                            {videoLessonAnalysis.map((analysis, index) => (
                                <Box
                                    key={index}
                                    mb={1}
                                >
                                    {getVisitedDate(analysis)}
                                </Box>
                            ))}
                        </Box>

                        <Box>
                            <Box sx={styles.timeTitleBox}>
                                <Box mr={1}>
                                    {formatMessage({ id: 'visitedTime' })}
                                </Box>

                                <Schedule
                                    fontSize="small"
                                    color="disabled"
                                />
                            </Box>

                            {videoLessonAnalysis.map((analysis, index) => (
                                <Box
                                    key={index}
                                    mb={1}
                                >
                                    {getVisitedTime(analysis)}
                                </Box>
                            ))}
                        </Box>

                        <Box>
                            <Box sx={styles.timeTitleBox}>
                                <Box mr={1}>
                                    {formatMessage({ id: 'duration' })}
                                </Box>

                                <Info
                                    fontSize="small"
                                    color="disabled"
                                />
                            </Box>

                            {videoLessonAnalysis.map((analysis, index) => (
                                <Box
                                    key={index}
                                    mb={1}
                                >
                                    {getDuration(analysis)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Collapse>
        </Box>
    )
}

export default Section
